<script lang="ts" setup>import { computed as _computed } from 'vue';

import { useI18n } from 'vue-i18n';
import type { ISOPRO, ISOPROProduct } from "~/models";
import { useProductsStore } from "~/store";
import ProductCard from "./ProductCard.vue";
import ProductPagination from "./ProductPagination.vue";
import ProductsFilter from "./ProductsFilter.vue";

const { t } = useI18n()

interface Props {
  modelValue: ISOPRO | null;
}

const store = useProductsStore();

const props = defineProps<Props>();

const products = _computed(() => store.products as ISOPRO[]);

const emits = defineEmits<{
  (e: "update:modelValue", product: ISOPROProduct | null): void;
}>();

function handleProductSelection(pageProducts: ISOPROProduct[]) {
  if (!pageProducts.some((p) => p.rowKey === props.modelValue?.rowKey)) {
    emits("update:modelValue", null);
  }
}
</script>

<template>
  <ProductsFilter
    :products="products"
    @emptyFilterList="emits('update:modelValue', null)"
    v-slot="{ filteredProducts }"
  >
    <ProductPagination
      v-if="filteredProducts.length"
      :filteredProducts="filteredProducts"
      @pageProductsChange="handleProductSelection($event)"
      v-slot="{ pageProducts }"
    >
      <div class="product-list-container" cypress-id="product-list-container">
          <ProductCard
            v-for="(product, i) in pageProducts"
            :class="{ active: modelValue?.rowKey === product.rowKey }"
            :key="product.rowKey"
            :index="i"
            :product="product"
            @selectedProduct="emits('update:modelValue', $event)"
          />
      </div>
    </ProductPagination>
    <ul v-else class="product-list">
      <li class="placeholder-item">{{ t("selectView.productSelect.noProduct") }}</li>
    </ul>
  </ProductsFilter>
</template>

<style lang="scss">
@use '../../../variables' as *;

.product-list {
  list-style: none;
  margin-bottom: var(--gutter-xl);
  padding: 0px 10px;
  max-height: 100%;
  text-align: center;

  &-container {
    display: grid;
    max-height: 100%;
    overflow-y: auto;
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 3px;
    grid-template-rows: max-content;
    max-height: 100%;
    @media screen and (max-width: $breakpoint-nav) {
      grid-template-columns: 1fr 1fr;
    }
  }

  .placeholder-item {
    cursor: default;
    font-style: italic;
    color: rgb(178, 178, 178);

    &:hover {
      background-color: unset;
      opacity: 1;
    }
  }
}
</style>
